import { Payment as PaymentIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import numeral from 'numeral';
import { useCallback, useMemo, useState } from 'react';
import { DARK_TEXT } from 'src/theme/palette';
import { ValidatePromoCodeDto } from 'woofwoof-api';
import { promoCodeApi } from '../../../../api/promo-code';
import { fDate } from '../../../../utils/formatTime';
import { CampaignFormValues } from '../CreateCampaignWizard';
import { translateKey } from 'src/locales/translate';


interface PromoCodeState {
  code: string;
  discountPercentage: number;
}

interface CampaignStep3SummaryProps {
  formValues: CampaignFormValues;
  onPayment: (promoCode?: string) => void;
  onBack: () => void;
  isLoading: boolean;
}

export default function CampaignStep3Summary({
  formValues,
  onPayment,
  onBack,
  isLoading,
}: CampaignStep3SummaryProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] =
    useState<PromoCodeState | null>(null);

  const { mutateAsync: applyPromoCode, isLoading: isPromoCodeLoading } =
    useMutation({
      mutationFn: (dto: ValidatePromoCodeDto) =>
        promoCodeApi.promoCodeControllerVerifyUser({
          validatePromoCodeDto: dto,
        }),
    });

  const totalBudget = useMemo(() => {
    if (!formValues.startDate || !formValues.endDate)
      return formValues.dailyBudget;
    const days = Math.max(
      1,
      Math.ceil(
        (formValues.endDate.getTime() - formValues.startDate.getTime()) /
          (1000 * 60 * 60 * 24),
      ),
    );
    return formValues.dailyBudget * days;
  }, [formValues.dailyBudget, formValues.startDate, formValues.endDate]);

  const discountAmount = useMemo(() => {
    if (!appliedPromoCode) return 0;
    return (totalBudget * appliedPromoCode.discountPercentage) / 100;
  }, [totalBudget, appliedPromoCode]);

  const finalAmount = useMemo(
    () => totalBudget - discountAmount,
    [totalBudget, discountAmount],
  );

  const handleApplyPromoCode = useCallback(async () => {
    if (!promoCodeInput.trim()) return;
    try {
      const response = await applyPromoCode({
        code: promoCodeInput.trim(),
        type: 'campaign' as ValidatePromoCodeDto['type'],
      });
      setAppliedPromoCode({
        code: promoCodeInput.trim(),
        discountPercentage: response.data.discountPercentage,
      });
      enqueueSnackbar(translateKey("snackbar.discountApplied"), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Kod rabatowy jest niepoprawny', { variant: 'error' });
    }
  }, [promoCodeInput, applyPromoCode, enqueueSnackbar]);

  const handleRemovePromoCode = useCallback(() => {
    setAppliedPromoCode(null);
    setPromoCodeInput('');
  }, []);

  const handlePayment = useCallback(() => {
    onPayment(appliedPromoCode?.code);
  }, [onPayment, appliedPromoCode]);

  const theme = useTheme();
  const isPromoCodeApplied = !!appliedPromoCode;

  return (
    <Stack spacing={3}>
      <Alert severity="info">{translateKey("hardcoded.sprawdz_szczegoly_kampanii_przed_dokonaniem_platnosci_po_oplaceniu")}</Alert>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Nazwa kampanii
        </Typography>
        <Typography variant="body1">{formValues.name}</Typography>
      </Box>

      {formValues.description && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>{translateKey("search.ui.aboutTab")}</Typography>
          <Typography variant="body1">{formValues.description}</Typography>
        </Box>
      )}

      <Divider />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{translateKey("hardcoded.slowa_kluczowe")}{formValues.keywords.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {formValues.keywords.map((keyword, index) => (
            <Chip key={index} label={keyword} size="small" variant="outlined" />
          ))}
        </Box>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Okres trwania
        </Typography>
        <Typography variant="body1">
          {formValues.startDate ? fDate(formValues.startDate) : '-'} -{' '}
          {formValues.endDate ? fDate(formValues.endDate) : '-'}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{translateKey("hardcoded.budzet_dzienny")}</Typography>
        <Typography variant="body1">
          {numeral(formValues.dailyBudget).format('0,0')}{translateKey("quickBooking.currencySuffix")}</Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{translateKey("quickBooking.promoField")}</Typography>
        <Stack direction="row" alignItems="center">
          <TextField
            value={promoCodeInput}
            onChange={(e) => setPromoCodeInput(e.target.value)}
            placeholder="Wpisz kod rabatowy"
            size="medium"
            disabled={isPromoCodeApplied}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          />
          <LoadingButton
            onClick={
              isPromoCodeApplied ? handleRemovePromoCode : handleApplyPromoCode
            }
            variant="contained"
            size="large"
            loading={isPromoCodeLoading}
            color={isPromoCodeApplied ? 'error' : 'primary'}
            sx={{
              borderTopLeftRadius: '0 !important',
              borderBottomLeftRadius: '0 !important',
              height: '56px',
              ...(!isPromoCodeApplied && {
                background: theme.palette.common.black,
                color: DARK_TEXT.primary,
              }),
            }}
          >
            {isPromoCodeApplied ? translateKey("common.remove") : translateKey("common.add")}
          </LoadingButton>
        </Stack>
        {isPromoCodeApplied && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>{translateKey("hardcoded.znizka")}{appliedPromoCode.discountPercentage}% (
            {numeral(discountAmount).format('0,0')}{translateKey("hardcoded.zl")}</Typography>
        )}
      </Box>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {isPromoCodeApplied
            ? translateKey("hardcoded.kwota_do_zaplaty_po_rabacie")
            : translateKey("hardcoded.szacowany_koszt_calkowity")}
        </Typography>
        {isPromoCodeApplied && (
          <Typography
            variant="h5"
            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
          >
            {numeral(totalBudget).format('0,0')}{translateKey("quickBooking.currencySuffix")}</Typography>
        )}
        <Typography variant="h4">
          {numeral(finalAmount).format('0,0')}{translateKey("quickBooking.currencySuffix")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{translateKey("hardcoded.kwota_obejmuje_calkowity_budzet_kampanii_na_caly_okres")}</Typography>
      </Alert>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onBack}
          disabled={isLoading}
        >{translateKey("quickBooking.back")}</Button>
        <LoadingButton
          variant="contained"
          color="primary"
          startIcon={<PaymentIcon />}
          onClick={handlePayment}
          loading={isLoading}
        >{translateKey("hardcoded.zaplac_i_aktywuj")}</LoadingButton>
      </Stack>
    </Stack>
  );
}
